import { useEffect, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { useTranslation } from '../../../../i18n';
import { Button } from '../../../../components/Shared/Button';
import { FormField } from '../../../../components/Shared/FormField';
import { Input } from '../../../../components/Shared/Input';
import {
  BusinessPlan,
  type CreateSubscriptionPlanRequest,
  type SubscriptionPlanDto,
  type UpdateSubscriptionPlanRequest,
} from '../../../../api/types/admin';

interface PlanFormProps {
  initial?: SubscriptionPlanDto | null;
  takenPlans: BusinessPlan[]; // BusinessPlans already used by other plans
  onSubmit: (
    data: CreateSubscriptionPlanRequest | UpdateSubscriptionPlanRequest,
  ) => Promise<boolean>;
  onCancel: () => void;
  loading?: boolean;
}

const BUSINESS_PLAN_OPTIONS: { value: BusinessPlan; key: string }[] = [
  { value: BusinessPlan.Free, key: '0' },
  { value: BusinessPlan.Starter, key: '1' },
  { value: BusinessPlan.Professional, key: '2' },
  { value: BusinessPlan.Enterprise, key: '3' },
];

export function PlanForm({
  initial,
  takenPlans,
  onSubmit,
  onCancel,
  loading,
}: PlanFormProps) {
  const { t } = useTranslation(['admin', 'common']);
  const isEdit = Boolean(initial);

  const [businessPlan, setBusinessPlan] = useState<BusinessPlan>(
    (initial?.businessPlan as BusinessPlan) ?? BusinessPlan.Starter,
  );
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [priceOnRequest, setPriceOnRequest] = useState(initial?.price == null);
  const [price, setPrice] = useState<string>(
    initial?.price != null ? String(initial.price) : '',
  );
  const [currency, setCurrency] = useState(initial?.currency ?? 'EUR');
  const [billingPeriodMonths, setBillingPeriodMonths] = useState<string>(
    String(initial?.billingPeriodMonths ?? 1),
  );
  const [unlimited, setUnlimited] = useState(initial?.maxEmployees == null);
  const [maxEmployees, setMaxEmployees] = useState<string>(
    initial?.maxEmployees != null ? String(initial.maxEmployees) : '',
  );
  const [features, setFeatures] = useState<string[]>(initial?.features ?? []);
  const [newFeature, setNewFeature] = useState('');
  const [isPublic, setIsPublic] = useState(initial?.isPublic ?? true);
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);
  const [displayOrder, setDisplayOrder] = useState<string>(
    String(initial?.displayOrder ?? 100),
  );

  useEffect(() => {
    if (initial) {
      setBusinessPlan((initial.businessPlan as BusinessPlan) ?? BusinessPlan.Starter);
      setName(initial.name ?? '');
      setDescription(initial.description ?? '');
      setPriceOnRequest(initial.price == null);
      setPrice(initial.price != null ? String(initial.price) : '');
      setCurrency(initial.currency ?? 'EUR');
      setBillingPeriodMonths(String(initial.billingPeriodMonths ?? 1));
      setUnlimited(initial.maxEmployees == null);
      setMaxEmployees(initial.maxEmployees != null ? String(initial.maxEmployees) : '');
      setFeatures(initial.features ?? []);
      setIsPublic(initial.isPublic ?? true);
      setIsActive(initial.isActive ?? true);
      setDisplayOrder(String(initial.displayOrder ?? 100));
    }
  }, [initial]);

  const addFeature = () => {
    const f = newFeature.trim();
    if (!f) return;
    if (!features.includes(f)) {
      setFeatures([...features, f]);
    }
    setNewFeature('');
  };

  const removeFeature = (f: string) => {
    setFeatures(features.filter((x) => x !== f));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const numericPrice = priceOnRequest || price === '' ? null : Number(price);
    const numericMax = unlimited || maxEmployees === '' ? null : Number(maxEmployees);

    if (isEdit) {
      const payload: UpdateSubscriptionPlanRequest = {
        name,
        description: description || null,
        price: numericPrice,
        currency,
        billingPeriodMonths: Number(billingPeriodMonths) || 1,
        maxEmployees: numericMax,
        features,
        isPublic,
        displayOrder: Number(displayOrder) || 0,
      };
      await onSubmit(payload);
    } else {
      const payload: CreateSubscriptionPlanRequest = {
        businessPlan,
        name,
        description: description || null,
        price: numericPrice,
        currency,
        billingPeriodMonths: Number(billingPeriodMonths) || 1,
        maxEmployees: numericMax,
        features,
        isPublic,
        displayOrder: Number(displayOrder) || 0,
      };
      await onSubmit(payload);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {!isEdit && (
        <FormField label={t('admin:plans.fields.businessPlan')} required>
          <select
            value={businessPlan}
            onChange={(e) => setBusinessPlan(Number(e.target.value) as BusinessPlan)}
            className="w-full px-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30"
          >
            {BUSINESS_PLAN_OPTIONS.filter(
              (o) => o.value === businessPlan || !takenPlans.includes(o.value),
            ).map((o) => (
              <option key={o.value} value={o.value}>
                {t(`admin:tenants.planNames.${o.key}`)}
              </option>
            ))}
          </select>
        </FormField>
      )}

      <FormField label={t('admin:plans.fields.name')} required>
        <Input value={name} onChange={(e) => setName(e.target.value)} required maxLength={100} />
      </FormField>

      <FormField label={t('admin:plans.fields.description')}>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          className="w-full px-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30"
        />
      </FormField>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FormField label={t('admin:plans.fields.price')}>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            disabled={priceOnRequest}
          />
          <label className="flex items-center gap-2 mt-2 text-[12px] text-slate-600">
            <input
              type="checkbox"
              checked={priceOnRequest}
              onChange={(e) => setPriceOnRequest(e.target.checked)}
              className="rounded border-slate-300"
            />
            {t('admin:plans.form.priceOnRequest')}
          </label>
        </FormField>
        <FormField label={t('admin:plans.fields.currency')}>
          <Input value={currency} onChange={(e) => setCurrency(e.target.value)} maxLength={3} />
        </FormField>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FormField label={t('admin:plans.fields.billingPeriod')}>
          <Input
            type="number"
            min="1"
            value={billingPeriodMonths}
            onChange={(e) => setBillingPeriodMonths(e.target.value)}
          />
        </FormField>
        <FormField label={t('admin:plans.fields.maxEmployees')}>
          <Input
            type="number"
            min="0"
            value={maxEmployees}
            onChange={(e) => setMaxEmployees(e.target.value)}
            disabled={unlimited}
          />
          <label className="flex items-center gap-2 mt-2 text-[12px] text-slate-600">
            <input
              type="checkbox"
              checked={unlimited}
              onChange={(e) => setUnlimited(e.target.checked)}
              className="rounded border-slate-300"
            />
            {t('admin:plans.form.unlimited')}
          </label>
        </FormField>
      </div>

      <FormField label={t('admin:plans.fields.features')}>
        <div className="space-y-2">
          {features.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {features.map((f) => (
                <span
                  key={f}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[12px]"
                >
                  {f}
                  <button
                    type="button"
                    onClick={() => removeFeature(f)}
                    className="text-slate-500 hover:text-red-600"
                    aria-label="Remove"
                  >
                    <X className="w-3 h-3" strokeWidth={2} />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <Input
              value={newFeature}
              onChange={(e) => setNewFeature(e.target.value)}
              placeholder={t('admin:plans.form.addFeaturePlaceholder')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addFeature();
                }
              }}
            />
            <Button
              type="button"
              variant="secondary"
              onClick={addFeature}
              leftIcon={<Plus className="w-3.5 h-3.5" strokeWidth={2} />}
            >
              {t('admin:plans.form.addFeature')}
            </Button>
          </div>
        </div>
      </FormField>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FormField label={t('admin:plans.fields.displayOrder')}>
          <Input
            type="number"
            value={displayOrder}
            onChange={(e) => setDisplayOrder(e.target.value)}
          />
        </FormField>
        <div className="space-y-2 sm:pt-6">
          <label className="flex items-center gap-2 text-[13px] text-slate-700">
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              className="rounded border-slate-300"
            />
            {t('admin:plans.fields.isPublic')}
          </label>
          {isEdit && (
            <label className="flex items-center gap-2 text-[13px] text-slate-700">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                disabled
                className="rounded border-slate-300"
              />
              <span className="text-slate-500">
                {t('admin:plans.fields.isActive')} ({t('common:label.readonly', { defaultValue: 'aktiv via Status-Toggle' })})
              </span>
            </label>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={loading}>
          {t('common:actions.cancel')}
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? t('common:actions.loading') : t('common:actions.save')}
        </Button>
      </div>
    </form>
  );
}
